import React from "react";
import { useParams } from "react-router-dom";
import { Tabs, Card, Title, Group, Badge, Text, Stack, Button, Table, Anchor } from "@mantine/core";
import { IconUsers, IconAward, IconCalendar, IconEdit, IconList, IconPlayerPlay, IconCheck, IconDownload, IconPaperclip } from "@tabler/icons-react";
import { MeetingDocumentsTab } from "../components/MeetingDocumentsTab";
import { AttendanceTab } from "../components/AttendanceTab";
import { AwardSheetTab } from "../components/AwardSheetTab";
import { AgendaReturnFormTab } from "../components/AgendaReturnFormTab";
import { useMeeting, useScheduleMeeting, useStartMeeting, useCEOEndorsementsByMeeting, useDownloadAgendaTenderDocument } from "../hooks";
import type { AgendaItem } from "../../../api/bidCommittee";
import { useAuth } from "../../../auth/AuthContext";
import { RoleGuard } from "../components/RoleGuard";
import { useNavigate } from "react-router-dom";

export const MeetingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: meeting, isLoading } = useMeeting(id);
  const scheduleMeeting = useScheduleMeeting();
  const startMeeting = useStartMeeting();
  const downloadTenderDocument = useDownloadAgendaTenderDocument();
  // const { user } = useAuth(); // Unused for now
  const navigate = useNavigate();
  const { data: ceoEndorsements } = useCEOEndorsementsByMeeting(id);
  
  const handleSchedule = () => {
    if (!id) return;
    scheduleMeeting.mutate({ id });
  };

  const handleStartMeeting = () => {
    if (!id) return;
    startMeeting.mutate({ id });
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!meeting) {
    return <div>Meeting not found</div>;
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "DRAFT":
        return "gray";
      case "SCHEDULED":
        return "blue";
      case "IN_PROGRESS":
        return "yellow";
      case "COMPLETED":
        return "green";
      case "CANCELLED":
        return "red";
      default:
        return "gray";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Stack gap="lg">
      <Card>
        <Group justify="space-between" mb="md">
          <div>
            <Title order={2}>{meeting.title}</Title>
            <Text c="dimmed" size="sm">
              Meeting #{meeting.meetingNumber}
            </Text>
          </div>
          <Group gap="md">
          <Badge color={getStatusColor(meeting.status)} size="lg">
            {meeting.status.replace("_", " ")}
          </Badge>
            {meeting.status === 'DRAFT' && (
              <RoleGuard allowedRoles={["PROCUREMENT_OFFICER", "HEAD_OF_PROCUREMENT", "ADMIN"]}>
                <Group gap="sm">
                  <Button
                    leftSection={<IconEdit size={16} />}
                    color="orange"
                    variant="light"
                    onClick={() => navigate(`/bid-committee/meetings/${meeting.id}/edit`)}
                  >
                    Edit Meeting
                  </Button>
                  <Button
                    leftSection={<IconCalendar size={16} />}
                    color="blue"
                    onClick={handleSchedule}
                    loading={scheduleMeeting.isPending}
                  >
                    Schedule Meeting
                  </Button>
                </Group>
              </RoleGuard>
            )}
            {meeting.status === 'SCHEDULED' && (
              <RoleGuard allowedRoles={["PROCUREMENT_OFFICER", "HEAD_OF_PROCUREMENT", "ADMIN"]}>
                <Group gap="sm">
                  <Button
                    leftSection={<IconEdit size={16} />}
                    color="orange"
                    variant="light"
                    onClick={() => navigate(`/bid-committee/meetings/${meeting.id}/edit`)}
                  >
                    Edit Meeting
                  </Button>
                  <Button
                    leftSection={<IconPlayerPlay size={16} />}
                    color="green"
                    onClick={handleStartMeeting}
                    loading={startMeeting.isPending}
                  >
                    Start Meeting
                  </Button>
                </Group>
              </RoleGuard>
            )}
          </Group>
        </Group>

        <Group gap="xl">
          <div>
            <Text size="sm" c="dimmed">
              Location
            </Text>
            <Text>{meeting.location}</Text>
          </div>
          <div>
            <Text size="sm" c="dimmed">
              Planned Start
            </Text>
            <Text>{formatDate(meeting.plannedStart)}</Text>
          </div>
          {meeting.actualStart && (
            <div>
              <Text size="sm" c="dimmed">
                Actual Start
              </Text>
              <Text>{formatDate(meeting.actualStart)}</Text>
            </div>
          )}
          {meeting.actualEnd && (
            <div>
              <Text size="sm" c="dimmed">
                Actual End
              </Text>
              <Text>{formatDate(meeting.actualEnd)}</Text>
            </div>
          )}
        </Group>
      </Card>

      <Tabs defaultValue={meeting.status === 'IN_PROGRESS' || meeting.status === 'COMPLETED' ? "attendance" : "agenda"}>
        <Tabs.List>
          {(meeting.status === 'IN_PROGRESS' || meeting.status === 'COMPLETED') && (
          <Tabs.Tab value="attendance" leftSection={<IconUsers size={16} />}>
            Attendance
            </Tabs.Tab>
          )}
          <Tabs.Tab value="agenda" leftSection={<IconList size={16} />}>
            Agenda ({meeting.agendaItems?.length || 0})
          </Tabs.Tab>
          <Tabs.Tab value="award-sheet" leftSection={<IconAward size={16} />}>
            Award sheets
          </Tabs.Tab>
          <Tabs.Tab value="return-forms" leftSection={<IconList size={16} />}>
            Return forms
          </Tabs.Tab>
          <Tabs.Tab value="ceo-endorsements" leftSection={<IconCheck size={16} />}>
            CEO Endorsements ({ceoEndorsements?.length || 0})
          </Tabs.Tab>
          <Tabs.Tab value="documents" leftSection={<IconDownload size={16} />}>
            Documents
          </Tabs.Tab>
        </Tabs.List>

        {(meeting.status === 'IN_PROGRESS' || meeting.status === 'COMPLETED') && (
        <Tabs.Panel value="attendance" pt="md">
          <AttendanceTab meetingId={meeting.id} />
          </Tabs.Panel>
        )}

        <Tabs.Panel value="agenda" pt="md">
          <Card shadow="sm" p="lg">
            <Title order={4} mb="md">Agenda Items</Title>
            {meeting.agendaItems && meeting.agendaItems.length > 0 ? (
              <Table striped highlightOnHover withTableBorder withColumnBorders>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: 80 }}>#</Table.Th>
                    <Table.Th>Agenda Item</Table.Th>
                    <Table.Th style={{ width: 220 }}>Linked Tender</Table.Th>
                    <Table.Th style={{ width: 220 }}>Tender Document</Table.Th>
                    <Table.Th style={{ width: 240 }}>Linked Single Source</Table.Th>
                    <Table.Th style={{ width: 220 }}>PR-03 Summary</Table.Th>
                    <Table.Th style={{ width: 180 }}>Award sheet</Table.Th>
                    <Table.Th style={{ width: 160 }}>Return form</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {meeting.agendaItems.map((item, index) => (
                    <Table.Tr key={item.id || index}>
                      <Table.Td>{item.orderNo || index + 1}</Table.Td>
                      <Table.Td>
                        <Text fw={700}>{item.subject || "-"}</Text>
                        {item.notes ? (
                          <Text size="sm" c="dimmed" mt={4}>
                            {item.notes}
                          </Text>
                        ) : (
                          <Text size="sm" c="dimmed" mt={4}>
                            -
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {item.tender ? (
                          <Badge
                            variant="light"
                            style={{ cursor: "pointer" }}
                            onClick={() => item.tender?.id && navigate(`/tenders/${item.tender.id}`)}
                          >
                            {item.tender.referenceNo}
                          </Badge>
                        ) : (
                          <Text size="sm" c="dimmed">-</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {item.tenderDocument ? (
                          <Group gap={4} wrap="nowrap">
                            <IconPaperclip size={14} />
                            <Anchor
                              component="button"
                              type="button"
                              size="sm"
                              style={{ wordBreak: "break-all", textAlign: "left" }}
                              onClick={() =>
                                item.tenderDocument &&
                                downloadTenderDocument.mutate({
                                  documentId: item.tenderDocument.id,
                                  fileName: item.tenderDocument.fileName,
                                })
                              }
                            >
                              {item.tenderDocument.fileName}
                            </Anchor>
                          </Group>
                        ) : (
                          <Text size="sm" c="dimmed">-</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {item.singleSource ? (
                          <Badge
                            color="grape"
                            variant="light"
                            style={{ cursor: "pointer" }}
                            onClick={() => item.singleSource?.id && navigate(`/single-source/${item.singleSource.id}`)}
                          >
                            {item.singleSource.formNumber}
                          </Badge>
                        ) : (
                          <Text size="sm" c="dimmed">-</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {(item as any).projectSummarySheet ? (
                          <Badge
                            color="teal"
                            variant="light"
                            style={{ cursor: "pointer" }}
                            onClick={() =>
                              (item as any).projectSummarySheet?.id &&
                              navigate(`/project-summary-sheets/${(item as any).projectSummarySheet.id}`)
                            }
                          >
                            {(item as any).projectSummarySheet.summarySheetNumber}
                          </Badge>
                        ) : (
                          <Text size="sm" c="dimmed">-</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {item.awardSheetLinks && item.awardSheetLinks.length > 0 ? (
                          <Badge
                            color="blue"
                            variant="light"
                            style={{ cursor: "pointer" }}
                            onClick={() =>
                              item.awardSheetLinks?.[0]?.awardSheetId &&
                              navigate(
                                `/bid-committee/award-sheets/${item.awardSheetLinks[0].awardSheetId}`
                              )
                            }
                          >
                            {item.awardSheetLinks[0].awardSheet?.awardSheetNumber ??
                              'Linked'}
                          </Badge>
                        ) : (
                          <Text size="sm" c="dimmed">-</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {(item as AgendaItem & { agendaReturnForm?: { id: string; formNumber?: string | null } }).agendaReturnForm ? (
                          <Badge
                            color="orange"
                            variant="light"
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              const rf = (item as AgendaItem & { agendaReturnForm?: { id: string } }).agendaReturnForm;
                              if (rf?.id) navigate(`/bid-committee/agenda-return-forms/${rf.id}`);
                            }}
                          >
                            {(item as AgendaItem & { agendaReturnForm?: { formNumber?: string | null } }).agendaReturnForm?.formNumber ?? "Linked"}
                          </Badge>
                        ) : (
                          <Text size="sm" c="dimmed">-</Text>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            ) : (
              <Text c="dimmed" ta="center" py="xl">
                No agenda items for this meeting.
              </Text>
            )}
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="award-sheet" pt="md">
          <AwardSheetTab meetingId={meeting.id} />
        </Tabs.Panel>

        <Tabs.Panel value="return-forms" pt="md">
          <AgendaReturnFormTab meetingId={meeting.id} />
        </Tabs.Panel>

        <Tabs.Panel value="documents" pt="md">
          <MeetingDocumentsTab meetingId={meeting.id} meetingNumber={meeting.meetingNumber} />
        </Tabs.Panel>

        <Tabs.Panel value="ceo-endorsements" pt="md">
          <Card shadow="sm" p="lg">
            <Stack gap="md">
              <Group justify="space-between">
                <Title order={4}>CEO Endorsements</Title>
                {meeting.agendaItems && meeting.agendaItems.length > 0 && (
                  <RoleGuard allowedRoles={["PROCUREMENT_OFFICER", "HEAD_OF_PROCUREMENT", "ADMIN"]}>
                    <Button
                      onClick={() => navigate(`/bid-committee/endorsements/new?meetingId=${meeting.id}`)}
                    >
                      Create Endorsement
                    </Button>
                  </RoleGuard>
                )}
              </Group>
              {ceoEndorsements && ceoEndorsements.length > 0 ? (
                <Stack gap="sm">
                  {ceoEndorsements.map((endorsement) => (
                    <Card
                      key={endorsement.id}
                      shadow="xs"
                      p="md"
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/bid-committee/endorsements/${endorsement.id}`)}
                    >
                      <Group justify="space-between">
                        <div>
                          <Text fw={500}>
                            Endorsement - {endorsement.items.length} item{endorsement.items.length !== 1 ? 's' : ''}
                          </Text>
                          <Text size="sm" c="dimmed">
                            Created: {formatDate(endorsement.createdAt)}
                          </Text>
                        </div>
                        <Group gap="xs">
                          <Badge
                            color={
                              endorsement.status === 'APPROVED' ? 'green' :
                              endorsement.status === 'REJECTED' ? 'red' :
                              endorsement.status === 'PENDING' ? 'yellow' : 'gray'
                            }
                            size="sm"
                          >
                            {endorsement.status}
                          </Badge>
                        </Group>
                      </Group>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <Text c="dimmed" ta="center" py="xl">
                  {meeting.agendaItems && meeting.agendaItems.length > 0
                    ? 'No CEO endorsements for this meeting.'
                    : 'This meeting has no agenda items. Add agenda items to create endorsements.'}
                </Text>
              )}
            </Stack>
          </Card>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
};
